/**
 * Where a floating bar sits over the sheet (pure — unit tested): the edit toolbar and the strip of
 * open songs float, so the page keeps the whole screen. A bar is docked to one edge — top / bottom
 * lie horizontally, left / right stand vertically — at a point along that edge (`along`: where its
 * centre sits, 0..1). Each bar may allow only some edges (the tab strip: top and bottom). Dragged by
 * its grip it follows the finger (followFinger) and, let go, settles on the allowed edge nearest the
 * finger (releaseDock); the grip's arrow keys move it too.
 * Remembered per device and bar (localStorage, never throws). The React side: lib/useFloatingDock.
 */

export type DockEdge = 'top' | 'bottom' | 'left' | 'right';

export interface Dock {
  edge: DockEdge;
  /** Centre of the bar along its edge, as a fraction of the container (0 = left / top). */
  along: number;
}

export const ALL_EDGES: readonly DockEdge[] = ['top', 'bottom', 'left', 'right'];
export const DEFAULT_DOCK: Dock = { edge: 'bottom', along: 0.5 };
/** Gap between a bar and the container's edges (CSS px). */
export const DOCK_MARGIN = 8;
/** One arrow-key step along the edge. */
export const DOCK_KEY_STEP = 0.1;

export const isVertical = (edge: DockEdge): boolean => edge === 'left' || edge === 'right';

const clamp01 = (v: number) => (Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 0.5);

/**
 * The dock for a finger at (x, y) in a w × h container: the nearest allowed edge (relative to the
 * container's size, so a wide screen does not favour top / bottom), and the finger's place along it.
 */
export function nearestDock(x: number, y: number, w: number, h: number, edges: readonly DockEdge[] = ALL_EDGES): Dock {
  const allowed = edges.length > 0 ? edges : ALL_EDGES;
  if (!(w > 0 && h > 0)) return allowed.includes(DEFAULT_DOCK.edge) ? DEFAULT_DOCK : { edge: allowed[0]!, along: 0.5 };
  const fx = clamp01(x / w);
  const fy = clamp01(y / h);
  const dist: Record<DockEdge, number> = { top: fy, bottom: 1 - fy, left: fx, right: 1 - fx };
  let edge = allowed[0]!;
  for (const e of allowed) if (dist[e] < dist[edge]) edge = e;
  return { edge, along: isVertical(edge) ? fy : fx };
}

export interface Placement {
  left: number;
  top: number;
}

/**
 * Top-left corner of a `size` bar docked at `dock` in a `box` container: against its edge
 * (DOCK_MARGIN away), centred on `along` but always inside the container (a bar larger than the
 * container starts at the margin; it scrolls inside its own max size).
 */
export function dockPlacement(dock: Dock, box: { w: number; h: number }, size: { w: number; h: number }, margin = DOCK_MARGIN): Placement {
  const fit = (centre: number, len: number, room: number) => Math.max(margin, Math.min(room - len - margin, centre - len / 2));
  switch (dock.edge) {
    case 'top':
      return { left: fit(dock.along * box.w, size.w, box.w), top: margin };
    case 'bottom':
      return { left: fit(dock.along * box.w, size.w, box.w), top: Math.max(margin, box.h - size.h - margin) };
    case 'left':
      return { left: margin, top: fit(dock.along * box.h, size.h, box.h) };
    case 'right':
      return { left: Math.max(margin, box.w - size.w - margin), top: fit(dock.along * box.h, size.h, box.h) };
  }
}

/**
 * Where a bar dragged by its grip goes while the finger moves: it follows the finger, keeping the
 * point it was grabbed at (`grab`: offset of the finger inside the bar), and stays inside the box.
 */
export function followFinger(
  finger: { x: number; y: number },
  grab: { x: number; y: number },
  box: { w: number; h: number },
  size: { w: number; h: number },
): Placement {
  const clamp = (v: number, max: number) => Math.max(0, Math.min(Math.max(0, max), v));
  return { left: clamp(finger.x - grab.x, box.w - size.w), top: clamp(finger.y - grab.y, box.h - size.h) };
}

/**
 * Where a dragged bar settles when the finger lifts: on the allowed edge nearest the FINGER (a wide
 * toolbar can always be taken to a side edge, though its centre cannot get near it), at the bar's
 * own place along that edge when it keeps its orientation (it settles where it was let go), else at
 * the finger's place (a bar that turns from lying to standing, or back, lands under the finger).
 */
export function releaseDock(
  current: Dock,
  finger: { x: number; y: number },
  bar: Placement & { w: number; h: number },
  box: { w: number; h: number },
  edges: readonly DockEdge[] = ALL_EDGES,
): Dock {
  const target = nearestDock(finger.x, finger.y, box.w, box.h, edges);
  if (isVertical(target.edge) !== isVertical(current.edge)) return target;
  const along = isVertical(target.edge) ? (bar.top + bar.h / 2) / box.h : (bar.left + bar.w / 2) / box.w;
  return { edge: target.edge, along: clamp01(along) };
}

/**
 * Arrow keys on the grip: along the edge it lies on (a step of DOCK_KEY_STEP) and, from the end of
 * that edge, round the corner (when that edge is allowed; else it stays at the end); an arrow
 * pointing across it moves it to the opposite edge (same place along it) when allowed. Other keys: null.
 */
export function dockByKey(dock: Dock, key: string, edges: readonly DockEdge[] = ALL_EDGES): Dock | null {
  const toward: Record<string, DockEdge> = { ArrowUp: 'top', ArrowDown: 'bottom', ArrowLeft: 'left', ArrowRight: 'right' };
  const target = toward[key];
  if (!target) return null;
  // An arrow pointing across the bar: over to that edge (its own edge, or one not allowed: stays).
  if (isVertical(dock.edge) === isVertical(target)) return target === dock.edge || !edges.includes(target) ? dock : { edge: target, along: dock.along };
  // The arrow runs along the edge: move; from the end of the edge, round the corner onto the edge
  // the arrow points at (at that corner).
  const dir = target === 'left' || target === 'top' ? -1 : 1;
  if ((dir < 0 && dock.along <= 0) || (dir > 0 && dock.along >= 1)) {
    if (!edges.includes(target)) return dock;
    const corner = dock.edge === 'bottom' || dock.edge === 'right' ? 1 : 0;
    return { edge: target, along: corner };
  }
  return { edge: dock.edge, along: Math.round(clamp01(dock.along + dir * DOCK_KEY_STEP) * 100) / 100 };
}

/** A stored dock; anything unreadable (or on an edge not allowed) gives `fallback`. */
export function parseDock(raw: unknown, fallback: Dock = DEFAULT_DOCK, edges: readonly DockEdge[] = ALL_EDGES): Dock {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return fallback;
  const r = raw as Record<string, unknown>;
  const edge = edges.includes(r.edge as DockEdge) ? (r.edge as DockEdge) : null;
  const along = typeof r.along === 'number' && Number.isFinite(r.along) ? clamp01(r.along) : null;
  return edge && along !== null ? { edge, along } : fallback;
}

/** The edit toolbar's stored dock (the tab strip has its own key). */
export const DOCK_STORAGE = 'kgr.annotate.dock';

export function loadDock(key: string = DOCK_STORAGE, fallback: Dock = DEFAULT_DOCK, edges: readonly DockEdge[] = ALL_EDGES): Dock {
  try {
    const raw = localStorage.getItem(key);
    return raw === null ? fallback : parseDock(JSON.parse(raw), fallback, edges);
  } catch {
    return fallback;
  }
}

export function saveDock(dock: Dock, key: string = DOCK_STORAGE): void {
  try {
    localStorage.setItem(key, JSON.stringify(dock));
  } catch {
    /* storage unavailable – lasts for this session only */
  }
}
